import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Layout,
  Drawer,
  Button,
  Input,
  Tooltip,
  Modal,
} from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  ApartmentOutlined,
  BookOutlined,
  IdcardOutlined,
  CloseOutlined,
  PlusOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";

// Password
import ChangeUserPassword from "./ChangeUserPassword";

// Todo
import PostTodoList from "./CalenderAndTodo/PostTodoList";

// Dashboard
import OverViewAdminOfficer from "./pages/OverViewAdminOfficer";

// User
import UserSearchBox from "../SchoolDashApp/UserDetails/UserSearchBox";
import UserDetailsPage from "../SchoolDashApp/UserDetails/UserDetailsPage";
import ProfileImage from "./ProfileImage";

// Students / Admissions
import AdmissionForm from "./NewStudents/AdmissionForm/AdmissionForm";
import StudentAdmission from "./NewStudents/AdmissionForm/StudentAdmission";

// Student Services
import CertificateDashboard from "./certificate/CertificateDashboard";
import StudentsIDCard from "../pages/Students/IdCards/StudentsIDCard";

// Student Management
import StudentStatusSummary from "../SchoolDashApp/StudentStatusSummary";
import ComplaintUnit from "../Dashboard/ComplaintUnit";

// Academic Reference
import ClassViewList from "../SchoolDashApp/Class/ClassViewList";
import ViewCourses from "../SchoolDashApp/Class/Subjects/ViewCourses";
import ApprovedLearningCourse from "./AdminApproveCource/ApprovedLearningCourse";

// Records
import SchoolArchiveDashboard from "./SchoolArchiveDashboard";

// System
import AddAcademyYear from "./AcademyYearSelection/AddAcademyYear";

// Appearance / Notifications
import Darkmode from "./Darkmode/Darkmode";
import NotificationIcon from "../components/ChatApp/NotificationIcon";
import Notice from "../components/ChatApp/Notice";

// Branding
import logo from "./tradeschoollogo.jpg";

const { Header, Sider, Content } = Layout;


/* =========================================================
   COLORS
========================================================= */

const COLORS = {
  primary: "#15803D",
  primaryDark: "#14532D",
  primaryDeep: "#052E16",

  secondary: "#166534",

  page: "#F4F8F5",
  pureWhite: "#FFFFFF",

  text: "#172B22",
  muted: "#64756B",
  border: "#DCE7DF",
  soft: "#F8FBF9",

  success: "#16805C",
  warning: "#C88719",
  error: "#C44545",
  info: "#2778A8",

  sidebar: "#07161D",
  sidebarAlt: "#0B2029",
  sidebarBorder: "#183844",
  sidebarText: "#D9E7EC",
  sidebarMuted: "#8FA8B3",
  sidebarIcon: "#8FB8C8",
  sidebarBright: "#4ADE80",

  gold: "#D4A017",
  yellow: "#FACC15",
  orange: "#C88719",
  green: "#16A34A",
  emerald: "#22C55E",
  sky: "#2778A8",

  antique: "#7893A0",

  black: "#07161D",
  white: "#F4F8FA",
  gray: "#A8BAC2",

  shadow: "0 12px 35px rgba(15,43,56,.08)",
};


/* =========================================================
   ADMISSION OFFICER MENU
========================================================= */

const menuItems = [
  {
    key: "overview",
    icon: <DashboardOutlined />,
    label: "Dashboard",
  },

  {
    key: "studentsadmission",
    icon: <UsergroupAddOutlined />,
    label: "Students Admission",
  },

  {
    key: "admissionlist",
    icon: <TeamOutlined />,
    label: "Students List",
  },

  {
    key: "statusrecord",
    icon: <SolutionOutlined />,
    label: "Students Status Record",
  },

  {
    key: "complaintunit",
    icon: <SafetyCertificateOutlined />,
    label: "Manage Students Complaint",
  },

  {
    key: "studentsidcards",
    icon: <IdcardOutlined />,
    label: "Generate ID Cards",
  },

  {
    key: "studentscertificate",
    icon: <SafetyCertificateOutlined />,
    label: "Students Certificate",
  },

  {
    key: "classviewlist",
    icon: <ApartmentOutlined />,
    label: "Grade Classes",
  },

  {
    key: "subjectviewlist",
    icon: <BookOutlined />,
    label: "Courses Listing",
  },

  {
    key: "approvedlearningcourse",
    icon: <BookOutlined />,
    label: "Approved Learning Course",
  },

  {
    key: "schoolarchivedashboard",
    icon: <TeamOutlined />,
    label: "School Archive Dashboard",
  },
];


/* =========================================================
   ADMISSION SIDEBAR SECTIONS
========================================================= */

const navigationSections = [
  {
    key: "command",
    title: "COMMAND CENTER",
    items: [
      "overview",
    ],
  },

  {
    key: "admissions",
    title: "ADMISSIONS & LEARNERS",
    items: [
      "studentsadmission",
      "admissionlist",
      "statusrecord",
      "complaintunit",
    ],
  },

  {
    key: "student-services",
    title: "STUDENT SERVICES",
    items: [
      "studentsidcards",
      "studentscertificate",
    ],
  },

  {
    key: "academic",
    title: "ACADEMIC REFERENCE",
    items: [
      "classviewlist",
      "subjectviewlist",
      "approvedlearningcourse",
    ],
  },

  {
    key: "records",
    title: "RECORDS",
    items: [
      "schoolarchivedashboard",
    ],
  },
];


/* =========================================================
   DASHBOARD
========================================================= */

const AdmissionOfficialDashboard = () => {
  const [collapsed, setCollapsed] =
    useState(false);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [darkMode, setDarkMode] =
    useState(false);

  const [activePage, setActivePage] =
    useState("overview");

  const [selectedUser, setSelectedUser] =
    useState(null);

  const [menuSearch, setMenuSearch] =
    useState("");

  const [todoOpen, setTodoOpen] =
    useState(false);

  const [passwordSettingOpen, setPasswordSettingOpen] =
    useState(false);

  const [isMobile, setIsMobile] =
    useState(
      typeof window !== "undefined"
        ? window.innerWidth < 900
        : false
    );

  const [isNavigating, setIsNavigating] =
    useState(false);

  const [pendingPage, setPendingPage] =
    useState(null);

  const [openAcademyYear, setOpenAcademyYear] =
    useState(false);

  const [academyYearRefreshing, setAcademyYearRefreshing] =
    useState(false);


  /* =========================================================
     RESPONSIVE SCREEN CONTROL
  ========================================================= */

  useEffect(() => {
    const handleResize = () => {
      const mobile =
        window.innerWidth < 900;

      setIsMobile(mobile);

      if (mobile) {
        setCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);


  /* =========================================================
     DASHBOARD STYLES
  ========================================================= */

  useEffect(() => {
    const style =
      document.createElement("style");

    style.id =
      "ignite-admission-dashboard-styles";

    style.innerHTML = `
      * {
        box-sizing: border-box;
      }

      html,
      body,
      #root {
        margin: 0;
        min-height: 100%;
      }

      body {
        overflow: hidden;
        background:
          linear-gradient(
            135deg,
            #F4F8F5 0%,
            #F8FAFB 48%,
            #F2F8F4 100%
          );
        color: #172B22;
      }

      ::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      ::-webkit-scrollbar-track {
        background: transparent;
      }

      ::-webkit-scrollbar-thumb {
        background: #9DB2BC;
        border-radius: 20px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: #15803D;
      }

      * {
        scrollbar-width: thin;
        scrollbar-color: #9DB2BC transparent;
      }

      /* =====================================================
         MENU BUTTON
      ===================================================== */

      .college-menu-button {
        width: 39px !important;
        height: 39px !important;
        min-width: 39px !important;
        padding: 0 !important;

        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        flex-shrink: 0 !important;

        border-radius: 9px !important;

        border:
          1px solid #DCE7DF !important;

        background:
          #FFFFFF !important;

        color:
          #14532D !important;

        box-shadow:
          none !important;

        transition:
          all 180ms ease !important;
      }

      .college-menu-button:hover {
        color:
          #15803D !important;

        border-color:
          #86C79A !important;

        background:
          #F0FAF3 !important;
      }

      .college-dark
      .college-menu-button {
        background:
          #14262E !important;

        border-color:
          #284651 !important;

        color:
          #D9E7EC !important;
      }


      /* =====================================================
         HEADER SEARCH
      ===================================================== */

      .college-search-box {
        min-width: 0;
      }

      .college-search-box
      .ant-input-affix-wrapper {
        min-height: 39px !important;

        border-radius: 9px !important;

        border:
          1px solid #DCE7DF !important;

        background:
          #FFFFFF !important;

        box-shadow:
          none !important;
      }

      .college-search-box
      .ant-input-affix-wrapper:hover {
        border-color:
          #86C79A !important;
      }

      .college-search-box
      .ant-input-affix-wrapper-focused {
        border-color:
          #15803D !important;

        box-shadow:
          0 0 0 2px
          rgba(21,128,61,.10)
          !important;
      }

      .college-search-box
      .ant-input {
        color:
          #172B22 !important;
      }

      .college-search-box
      .ant-input::placeholder {
        color:
          #81919B !important;
      }

      .college-dark
      .college-search-box
      .ant-input-affix-wrapper {
        background:
          #14262E !important;

        border-color:
          #284651 !important;
      }

      .college-dark
      .college-search-box
      .ant-input {
        color:
          #F3F8FA !important;
      }


      /* =====================================================
         SIDEBAR SEARCH
      ===================================================== */

      .college-sidebar-search
      .ant-input-affix-wrapper {
        min-height: 39px !important;

        border-radius: 8px !important;

        background:
          #0D232C !important;

        border:
          1px solid #244550 !important;

        box-shadow:
          none !important;
      }

      .college-sidebar-search
      .ant-input-affix-wrapper:hover {
        border-color:
          #287A48 !important;
      }

      .college-sidebar-search
      .ant-input {
        background:
          transparent !important;

        color:
          #E4F0F4 !important;
      }

      .college-sidebar-search
      .ant-input::placeholder {
        color:
          #78939E !important;
      }

      .college-sidebar-search
      .ant-input-affix-wrapper-focused {
        border-color:
          #22C55E !important;

        box-shadow:
          0 0 0 2px
          rgba(34,197,94,.16)
          !important;
      }


      /* =====================================================
         SIDEBAR SCROLL
      ===================================================== */

      .college-sidebar-scroll {
        flex: 1;

        min-height: 0;

        overflow-y: auto;
        overflow-x: hidden;

        padding-bottom: 28px;

        scrollbar-width: thin;

        scrollbar-color:
          rgba(74,222,128,.48)
          transparent;
      }

      .college-sidebar-scroll::-webkit-scrollbar {
        width: 6px;
      }

      .college-sidebar-scroll::-webkit-scrollbar-track {
        background: transparent;
      }

      .college-sidebar-scroll::-webkit-scrollbar-thumb {
        background:
          linear-gradient(
            180deg,
            rgba(74,222,128,.65),
            rgba(212,160,23,.55)
          );

        border-radius: 20px;
      }

      .college-sidebar-scroll::-webkit-scrollbar-thumb:hover {
        background:
          linear-gradient(
            180deg,
            #4ADE80,
            #D4A017
          );
      }


      /* =====================================================
         SECTION TITLES
      ===================================================== */

      .college-section-title {
        padding:
          16px 18px 7px;

        color:
          #6F8C99;

        font-size:
          9px;

        font-weight:
          800;

        letter-spacing:
          1.25px;

        user-select:
          none;
      }


      /* =====================================================
         NAVIGATION
      ===================================================== */

      .college-nav-item {
        position:
          relative;

        width:
          calc(100% - 24px);

        min-height:
          43px;

        margin:
          2px 12px;

        padding:
          0 12px;

        border:
          0;

        border-radius:
          8px;

        display:
          flex;

        align-items:
          center;

        gap:
          11px;

        color:
          #C7D8DE;

        background:
          transparent;

        cursor:
          pointer;

        text-align:
          left;

        font-family:
          inherit;

        font-size:
          13px;

        font-weight:
          500;

        transition:
          background 150ms ease,
          color 150ms ease,
          transform 150ms ease;
      }

      .college-nav-item:hover {
        background:
          linear-gradient(
            90deg,
            rgba(21,128,61,.32),
            rgba(21,128,61,.09)
          );

        color:
          #FFFFFF;
      }

      .college-nav-item:hover
      .college-nav-icon {
        color:
          #4ADE80;

        border-color:
          rgba(74,222,128,.20);

        background:
          rgba(74,222,128,.08);
      }

      .college-nav-item.active {
        background:
          linear-gradient(
            90deg,
            rgba(21,128,61,.58),
            rgba(21,128,61,.24),
            rgba(5,46,22,.08)
          );

        color:
          #FFFFFF;

        font-weight:
          700;

        box-shadow:
          0 6px 18px
          rgba(0,0,0,.18);
      }

      .college-nav-item.active::before {
        content:
          "";

        position:
          absolute;

        left:
          0;

        top:
          8px;

        bottom:
          8px;

        width:
          3px;

        border-radius:
          0 3px 3px 0;

        background:
          linear-gradient(
            180deg,
            #4ADE80,
            #FACC15
          );

        box-shadow:
          0 0 10px
          rgba(74,222,128,.55);
      }

      .college-nav-item.active
      .college-nav-icon {
        color:
          #4ADE80;

        border-color:
          rgba(74,222,128,.25);

        background:
          rgba(74,222,128,.10);
      }

      .college-nav-item:disabled {
        cursor:
          wait;

        opacity:
          .72;
      }

      .college-nav-icon {
        width:
          28px;

        height:
          28px;

        min-width:
          28px;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        border-radius:
          7px;

        color:
          #8FB8C8;

        border:
          1px solid transparent;

        font-size:
          15px;

        transition:
          all 150ms ease;
      }

      .college-nav-label {
        overflow:
          hidden;

        white-space:
          nowrap;

        text-overflow:
          ellipsis;

        line-height:
          1.2;
      }

      .college-nav-chevron {
        margin-left:
          auto;

        opacity:
          0;

        transform:
          translateX(-3px);

        transition:
          all 150ms ease;

        color:
          #4ADE80;
      }

      .college-nav-item:hover
      .college-nav-chevron,

      .college-nav-item.active
      .college-nav-chevron {
        opacity:
          1;

        transform:
          translateX(0);
      }

      .college-collapsed-item {
        width:
          calc(100% - 18px);

        justify-content:
          center;

        margin-left:
          9px;

        margin-right:
          9px;

        padding:
          0;
      }

      .college-collapsed-item
      .college-nav-chevron {
        display:
          none;
      }

      .college-collapsed-item
      .college-nav-label {
        display:
          none;
      }

      .college-collapsed-item
      .college-nav-icon {
        width:
          34px;

        height:
          34px;

        min-width:
          34px;
      }


      /* =====================================================
         HEADER ACTIONS
      ===================================================== */

      .college-header-actions {
        display:
          flex;

        align-items:
          center;

        justify-content:
          flex-end;

        gap:
          8px;

        flex-shrink:
          0;

        min-width:
          max-content;
      }

      .college-header-action {
        width:
          39px;

        height:
          39px;

        min-width:
          39px;

        padding:
          0;

        border-radius:
          9px;

        border:
          1px solid #DCE7DF;

        background:
          #FFFFFF;

        color:
          #365B6D;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        cursor:
          pointer;

        transition:
          all 160ms ease;

        flex-shrink:
          0;
      }

      .college-header-action:hover {
        color:
          #15803D;

        border-color:
          #86C79A;

        background:
          #F0FAF3;
      }

      .college-dark
      .college-header-action {
        background:
          #14262E;

        border-color:
          #284651;

        color:
          #D9E7EC;
      }


      /* =====================================================
         ACADEMIC YEAR BUTTON
      ===================================================== */

      .college-academic-year {
        height:
          39px;

        min-width:
          39px;

        padding:
          0 13px;

        border-radius:
          9px;

        border:
          1px solid #DCE7DF;

        background:
          #FFFFFF;

        color:
          #365B6D;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        gap:
          7px;

        cursor:
          pointer;

        font-size:
          11px;

        font-weight:
          700;

        white-space:
          nowrap;

        transition:
          all 160ms ease;

        flex-shrink:
          0;
      }

      .college-academic-year:hover {
        color:
          #15803D;

        border-color:
          #86C79A;

        background:
          #F0FAF3;
      }

      .college-academic-year:disabled {
        opacity:
          .65;

        cursor:
          not-allowed;
      }

      .college-dark
      .college-academic-year {
        background:
          #14262E;

        border-color:
          #284651;

        color:
          #D9E7EC;
      }


      /* =====================================================
         PROFILE
      ===================================================== */

      .college-profile-container {
        width:
          40px;

        height:
          40px;

        min-width:
          40px;

        border-radius:
          50%;

        overflow:
          hidden;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        border:
          2px solid #15803D;

        background:
          #FFFFFF;

        box-shadow:
          0 3px 12px
          rgba(15,43,56,.14);

        position:
          relative;

        flex-shrink:
          0;
      }

      .college-profile-avatar {
        width:
          100%;

        height:
          100%;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        overflow:
          hidden;

        border-radius:
          50%;
      }

      .college-profile-container > *,
      .college-profile-avatar > * {
        width:
          100% !important;

        height:
          100% !important;

        min-width:
          0 !important;

        min-height:
          0 !important;

        margin:
          0 !important;

        padding:
          0 !important;

        display:
          flex !important;

        align-items:
          center !important;

        justify-content:
          center !important;
      }

      .college-profile-container img,
      .college-profile-avatar img {
        width:
          100% !important;

        height:
          100% !important;

        min-width:
          100% !important;

        min-height:
          100% !important;

        object-fit:
          cover !important;

        object-position:
          center !important;

        display:
          block !important;

        margin:
          0 !important;

        padding:
          0 !important;
      }


      /* =====================================================
         WORKSPACE
      ===================================================== */

      .college-workspace {
        border:
          1px solid #DCE7DF;

        background:
          #FFFFFF;

        border-radius:
          11px;

        min-height:
          calc(100vh - 111px);

        box-shadow:
          0 4px 18px
          rgba(15,43,56,.035);

        position:
          relative;

        overflow:
          hidden;
      }

      .college-workspace::before {
        content:
          "";

        position:
          absolute;

        top:
          0;

        left:
          0;

        right:
          0;

        height:
          2px;

        background:
          linear-gradient(
            90deg,
            #15803D 0%,
            #22C55E 55%,
            #D4A017 100%
          );

        opacity:
          .65;

        pointer-events:
          none;
      }

      .college-workspace-dark {
        background:
          #14262E;

        border-color:
          #284651;
      }

      .college-workspace-dark::before {
        opacity:
          .8;
      }


      /* =====================================================
         NAVIGATION PROGRESS
      ===================================================== */

      .college-navigation-progress {
        position:
          absolute;

        top:
          0;

        left:
          0;

        right:
          0;

        height:
          3px;

        z-index:
          1000;

        overflow:
          hidden;

        background:
          rgba(21,128,61,.12);
      }

      .college-navigation-progress::after {
        content:
          "";

        position:
          absolute;

        top:
          0;

        left:
          0;

        width:
          0;

        height:
          100%;

        background:
          linear-gradient(
            90deg,
            #14532D,
            #22C55E,
            #FACC15
          );

        box-shadow:
          0 0 8px
          rgba(34,197,94,.45);

        animation:
          collegeNavigationProgress
          2s linear forwards;
      }

      @keyframes collegeNavigationProgress {
        0% {
          width:
            0%;
        }

        15% {
          width:
            22%;
        }

        45% {
          width:
            48%;
        }

        70% {
          width:
            70%;
        }

        90% {
          width:
            88%;
        }

        100% {
          width:
            100%;
        }
      }

      .college-loading-label {
        position:
          absolute;

        top:
          11px;

        left:
          50%;

        transform:
          translateX(-50%);

        z-index:
          1001;

        padding:
          5px 10px;

        border-radius:
          6px;

        background:
          linear-gradient(
            135deg,
            rgba(5,46,22,.98),
            rgba(20,83,45,.98)
          );

        color:
          #FFFFFF;

        font-size:
          9px;

        font-weight:
          700;

        letter-spacing:
          .75px;

        text-transform:
          uppercase;

        white-space:
          nowrap;

        box-shadow:
          0 5px 15px
          rgba(0,0,0,.18);
      }


      /* =====================================================
         PAGE HEADING
      ===================================================== */

      .college-page-heading {
        min-height:
          34px;

        display:
          flex;

        align-items:
          center;

        justify-content:
          space-between;

        gap:
          12px;

        margin-bottom:
          8px;

        padding:
          0 2px;
      }

      .college-page-heading-left {
        min-width:
          0;

        display:
          flex;

        align-items:
          center;

        gap:
          8px;
      }

      .college-page-heading-line {
        width:
          4px;

        height:
          18px;

        min-width:
          4px;

        border-radius:
          3px;

        background:
          linear-gradient(
            180deg,
            #15803D,
            #D4A017
          );

        box-shadow:
          0 0 7px
          rgba(21,128,61,.22);
      }

      .college-page-heading-title {
        overflow:
          hidden;

        text-overflow:
          ellipsis;

        white-space:
          nowrap;

        color:
          #172B22;

        font-size:
          14px;

        font-weight:
          750;
      }

      .college-dark
      .college-page-heading-title {
        color:
          #F3F8FA;
      }

      .college-page-heading-meta {
        color:
          #64756B;

        font-size:
          10px;

        font-weight:
          600;

        white-space:
          nowrap;

        letter-spacing:
          .2px;
      }

      .college-dark
      .college-page-heading-meta {
        color:
          #9FB5BF;
      }


      /* =====================================================
         SIDEBAR
      ===================================================== */

      .college-sidebar-shell {
        position:
          relative;

        height:
          100vh;

        min-height:
          100vh;

        overflow:
          hidden;

        background:
          radial-gradient(
            circle at 18% 12%,
            rgba(34,197,94,.10),
            transparent 24%
          ),
          radial-gradient(
            circle at 88% 82%,
            rgba(212,160,23,.08),
            transparent 25%
          ),
          linear-gradient(
            145deg,
            #07161D 0%,
            #091B23 46%,
            #061A10 100%
          );
      }

      .college-sidebar-shell::before {
        content:
          "";

        position:
          absolute;

        inset:
          0;

        pointer-events:
          none;

        background:
          radial-gradient(
            circle at 14% 22%,
            rgba(74,222,128,.14) 0,
            rgba(74,222,128,.07) 3px,
            transparent 10px
          ),
          radial-gradient(
            circle at 82% 18%,
            rgba(250,204,21,.10) 0,
            rgba(250,204,21,.05) 4px,
            transparent 12px
          ),
          radial-gradient(
            circle at 72% 56%,
            rgba(74,222,128,.08) 0,
            rgba(74,222,128,.04) 4px,
            transparent 13px
          ),
          radial-gradient(
            circle at 24% 78%,
            rgba(250,204,21,.07) 0,
            rgba(250,204,21,.035) 3px,
            transparent 11px
          ),
          radial-gradient(
            circle at 91% 88%,
            rgba(74,222,128,.10) 0,
            rgba(74,222,128,.04) 3px,
            transparent 11px
          );

        animation:
          collegeSidebarGlow
          12s ease-in-out infinite alternate;

        opacity:
          .9;
      }

      .college-sidebar-shell::after {
        content:
          "";

        position:
          absolute;

        top:
          0;

        right:
          0;

        bottom:
          0;

        width:
          1px;

        pointer-events:
          none;

        background:
          linear-gradient(
            180deg,
            rgba(34,197,94,.08),
            rgba(34,197,94,.30),
            rgba(250,204,21,.18),
            rgba(34,197,94,.06)
          );

        box-shadow:
          0 0 18px
          rgba(34,197,94,.08);
      }

      @keyframes collegeSidebarGlow {
        0% {
          transform:
            translate3d(-8px,-5px,0)
            scale(1);

          opacity:
            .65;
        }

        50% {
          transform:
            translate3d(10px,7px,0)
            scale(1.04);

          opacity:
            .95;
        }

        100% {
          transform:
            translate3d(-4px,11px,0)
            scale(1.02);

          opacity:
            .72;
        }
      }

      .college-sidebar-content {
        position:
          relative;

        z-index:
          2;

        height:
          100%;

        min-height:
          100vh;

        display:
          flex;

        flex-direction:
          column;

        overflow:
          hidden;
      }


      /* =====================================================
         PASSWORD MODAL
      ===================================================== */

      .college-password-modal
      .ant-modal-content {
        border-radius:
          15px !important;

        overflow:
          hidden !important;

        padding:
          0 !important;
      }

      .college-password-modal
      .ant-modal-body {
        padding:
          0 !important;
      }


      /* =====================================================
         RESPONSIVE
      ===================================================== */

      @media (prefers-reduced-motion: reduce) {
        .college-sidebar-shell::before {
          animation:
            none;
        }

        .college-navigation-progress::after {
          animation:
            none;

          width:
            100%;
        }
      }

      @media (max-width: 899px) {
        .college-header-actions {
          gap:
            6px;
        }

        .college-academic-year {
          width:
            39px;

          padding:
            0;
        }

        .college-academic-year-text {
          display:
            none;
        }

        .college-profile-container {
          width:
            36px;

          height:
            36px;

          min-width:
            36px;
        }

        .college-workspace {
          min-height:
            calc(100vh - 96px);

          border-radius:
            8px;
        }

        .college-page-heading {
          min-height:
            30px;
        }

        .college-page-heading-title {
          font-size:
            13px;
        }

        .college-page-heading-meta {
          display:
            none;
        }
      }

      @media (max-width: 650px) {
        .college-search-box {
          max-width:
            none !important;

          flex:
            1 1 auto !important;
        }

        .college-header-actions {
          gap:
            5px;
        }

        .college-header-action {
          width:
            36px;

          height:
            36px;

          min-width:
            36px;
        }

        .college-academic-year {
          width:
            36px;

          height:
            36px;

          min-width:
            36px;
        }

        .college-profile-container {
          width:
            34px;

          height:
            34px;

          min-width:
            34px;
        }

        .college-menu-button {
          width:
            36px !important;

          height:
            36px !important;

          min-width:
            36px !important;
        }
      }

      @media (max-width: 480px) {
        .college-header-actions {
          gap:
            4px;
        }

        .college-header-action,
        .college-academic-year {
          width:
            34px;

          height:
            34px;

          min-width:
            34px;
        }

        .college-profile-container {
          width:
            32px;

          height:
            32px;

          min-width:
            32px;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      const existing =
        document.getElementById(
          "ignite-admission-dashboard-styles"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, []);


  /* =========================================================
     THEME
  ========================================================= */

  const theme = useMemo(
    () => ({
      darkMode,

      background: darkMode
        ? "#09171E"
        : COLORS.page,

      sidebar:
        COLORS.sidebar,

      header: darkMode
        ? "#10232C"
        : COLORS.pureWhite,

      card: darkMode
        ? "#14262E"
        : COLORS.pureWhite,

      inputBackground: darkMode
        ? "#14262E"
        : COLORS.pureWhite,

      text: darkMode
        ? "#F3F8FA"
        : COLORS.text,

      secondary: darkMode
        ? "#9FB5BF"
        : COLORS.muted,

      border: darkMode
        ? "#284651"
        : COLORS.border,

      primary:
        COLORS.primary,

      navy:
        COLORS.sidebar,

      blue:
        COLORS.secondary,

      gold:
        COLORS.gold,

      yellow:
        COLORS.yellow,

      orange:
        COLORS.orange,

      green:
        COLORS.green,

      sky:
        COLORS.sky,

      antique:
        COLORS.antique,
    }),
    [darkMode]
  );


  /* =========================================================
     MENU LOOKUP
  ========================================================= */

  const menuLookup = useMemo(
    () =>
      Object.fromEntries(
        menuItems.map(
          (item) => [
            item.key,
            item,
          ]
        )
      ),
    []
  );


  /* =========================================================
     SIDEBAR SEARCH
  ========================================================= */

  const filteredSections =
    useMemo(() => {
      const query =
        menuSearch
          .trim()
          .toLowerCase();

      if (!query) {
        return navigationSections;
      }

      return navigationSections
        .map((section) => ({
          ...section,

          items:
            section.items.filter(
              (key) =>
                menuLookup[key]
                  ?.label
                  ?.toLowerCase()
                  .includes(query)
            ),
        }))
        .filter(
          (section) =>
            section.items.length >
            0
        );
    }, [
      menuSearch,
      menuLookup,
    ]);


  /* =========================================================
     ACTIVE PAGE TITLE
  ========================================================= */

  const activeTitle =
    selectedUser
      ? "User Details"
      : menuLookup[
          activePage
        ]?.label ||
        "Dashboard";


  /* =========================================================
     ACADEMIC YEAR REFRESH
  ========================================================= */

  const fetchAcademicYears =
    async () => {
      setAcademyYearRefreshing(
        true
      );

      try {
        window.dispatchEvent(
          new Event(
            "academicYearUpdated"
          )
        );

        window.dispatchEvent(
          new Event(
            "academic-year-updated"
          )
        );
      } finally {
        setTimeout(() => {
          setAcademyYearRefreshing(
            false
          );
        }, 400);
      }
    };


  /* =========================================================
     PAGE RENDERING
  ========================================================= */

  const renderPage = () => {
    if (selectedUser) {
      return (
        <UserDetailsPage
          theme={theme}
          selectedUser={
            selectedUser
          }
        />
      );
    }

    switch (activePage) {
      /* -----------------------------------------------------
         DASHBOARD
      ----------------------------------------------------- */

      case "overview":
        return (
          <OverViewAdminOfficer
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         ADMISSIONS
      ----------------------------------------------------- */

      case "studentsadmission":
        return (
          <AdmissionForm
            theme={theme}
          />
        );

      case "admissionlist":
        return (
          <StudentAdmission
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         STUDENT MANAGEMENT
      ----------------------------------------------------- */

      case "statusrecord":
        return (
          <StudentStatusSummary
            theme={theme}
          />
        );

      case "complaintunit":
        return (
          <ComplaintUnit
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         STUDENT SERVICES
      ----------------------------------------------------- */

      case "studentsidcards":
        return (
          <StudentsIDCard
            theme={theme}
          />
        );

      case "studentscertificate":
        return (
          <CertificateDashboard
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         ACADEMIC REFERENCE
      ----------------------------------------------------- */

      case "classviewlist":
        return (
          <ClassViewList
            theme={theme}
          />
        );

      case "subjectviewlist":
        return (
          <ViewCourses
            theme={theme}
          />
        );

      case "approvedlearningcourse":
        return (
          <ApprovedLearningCourse
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         RECORDS
      ----------------------------------------------------- */

      case "schoolarchivedashboard":
        return (
          <SchoolArchiveDashboard
            theme={theme}
          />
        );


      /* -----------------------------------------------------
         FALLBACK
      ----------------------------------------------------- */

      default:
        return (
          <OverViewAdminOfficer
            theme={theme}
          />
        );
    }
  };


  /* =========================================================
     NAVIGATION
  ========================================================= */

  const handleNavigation =
    (key) => {
      if (isNavigating) {
        return;
      }

      if (
        key === activePage &&
        !selectedUser
      ) {
        if (isMobile) {
          setDrawerOpen(false);
        }

        return;
      }

      setPendingPage(key);

      setIsNavigating(true);

      setSelectedUser(null);

      setMenuSearch("");

      if (isMobile) {
        setDrawerOpen(false);
      }

      window.setTimeout(() => {
        setActivePage(key);

        setPendingPage(null);

        setIsNavigating(false);
      }, 2000);
    };


  /* =========================================================
     SIDEBAR NAV ITEM
  ========================================================= */

  const renderNavigationItem =
    (item) => {
      const active =
        activePage ===
          item.key &&
        !selectedUser;

      return (
        <Tooltip
          key={item.key}
          title={
            collapsed
              ? item.label
              : ""
          }
          placement="right"
        >
          <button
            type="button"
            className={[
              "college-nav-item",

              active
                ? "active"
                : "",

              collapsed
                ? "college-collapsed-item"
                : "",
            ].join(" ")}
            onClick={() =>
              handleNavigation(
                item.key
              )
            }
            disabled={
              isNavigating
            }
            aria-current={
              active
                ? "page"
                : undefined
            }
          >
            <span className="college-nav-icon">
              {item.icon}
            </span>

            <span className="college-nav-label">
              {item.label}
            </span>

            <span className="college-nav-chevron">
              <span
                style={{
                  display:
                    "flex",
                  fontSize: 9,
                }}
              >
                ›
              </span>
            </span>
          </button>
        </Tooltip>
      );
    };


  /* =========================================================
     SIDEBAR
  ========================================================= */

  const sidebar = (
    <div className="college-sidebar-shell">
      <div className="college-sidebar-content">

        <div
          style={{
            height: "100%",
            minHeight: "100vh",

            display: "flex",
            flexDirection:
              "column",

            background:
              "transparent",
          }}
        >

          {/* -------------------------------------------------
             LOGO / BRAND
          ------------------------------------------------- */}

          <div
            style={{
              height: 72,
              minHeight: 72,

              display: "flex",
              alignItems:
                "center",

              justifyContent:
                collapsed
                  ? "center"
                  : "flex-start",

              gap: 11,

              padding:
                collapsed
                  ? "0 10px"
                  : "0 18px",

              borderBottom:
                "1px solid rgba(74,222,128,.10)",

              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                minWidth: 38,

                borderRadius: 8,

                padding: 2,

                background:
                  "#FFFFFF",

                display: "flex",
                alignItems:
                  "center",

                justifyContent:
                  "center",

                overflow:
                  "hidden",

                border:
                  "1px solid rgba(74,222,128,.30)",

                boxShadow:
                  "0 4px 14px rgba(0,0,0,.25)",
              }}
            >
              <img
                src={logo}
                alt="Ignite Skills Training Center"
                style={{
                  width:
                    "100%",

                  height:
                    "100%",

                  objectFit:
                    "cover",

                  borderRadius:
                    6,

                  display:
                    "block",
                }}
              />
            </div>

            {!collapsed && (
              <div
                style={{
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    color:
                      "#F4F9FB",

                    fontSize:
                      13,

                    fontWeight:
                      800,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",

                    letterSpacing:
                      ".15px",
                  }}
                >
                  IGNITE
                </div>

                <div
                  style={{
                    marginTop:
                      3,

                    color:
                      "#4ADE80",

                    fontSize:
                      8,

                    fontWeight:
                      800,

                    letterSpacing:
                      1.05,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  }}
                >
                  ADMISSION OFFICE
                </div>
              </div>
            )}
          </div>


          {/* -------------------------------------------------
             MODULE SEARCH
          ------------------------------------------------- */}

          {!collapsed && (
            <div
              className="college-sidebar-search"
              style={{
                padding:
                  "13px 12px 6px",

                flexShrink:
                  0,
              }}
            >
              <Input
                allowClear
                value={
                  menuSearch
                }
                onChange={(event) =>
                  setMenuSearch(
                    event.target
                      .value
                  )
                }
                placeholder="Find an admission module..."
                prefix={
                  <SearchOutlined
                    style={{
                      color:
                        COLORS.sidebarBright,
                    }}
                  />
                }
              />
            </div>
          )}


          {/* -------------------------------------------------
             NAVIGATION
          ------------------------------------------------- */}

          <div className="college-sidebar-scroll">

            {filteredSections.length ===
              0 && (
              <div
                style={{
                  padding:
                    "35px 20px",

                  textAlign:
                    "center",

                  color:
                    "#78939E",

                  fontSize:
                    11,
                }}
              >
                No admission module found
              </div>
            )}

            {filteredSections.map(
              (section) => {
                const items =
                  section.items
                    .map(
                      (key) =>
                        menuLookup[
                          key
                        ]
                    )
                    .filter(
                      Boolean
                    );

                if (
                  !items.length
                ) {
                  return null;
                }

                return (
                  <div
                    key={
                      section.key
                    }
                  >

                    {!collapsed ? (
                      <div className="college-section-title">
                        {
                          section.title
                        }
                      </div>
                    ) : (
                      <div
                        style={{
                          height:
                            12,
                        }}
                      />
                    )}

                    {items.map(
                      renderNavigationItem
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );


  /* =========================================================
     MAIN DASHBOARD
  ========================================================= */

  return (
    <div
      className={
        darkMode
          ? "college-dark"
          : ""
      }
      style={{
        position:
          "fixed",

        inset:
          0,

        zIndex:
          999,

        overflow:
          "hidden",

        background:
          theme.background,
      }}
    >

      <Layout
        style={{
          height:
            "100vh",

          minHeight:
            "100vh",

          background:
            theme.background,
        }}
      >

        {/* =================================================
           DESKTOP SIDEBAR
        ================================================= */}

        {!isMobile && (
          <Sider
            width={292}
            collapsedWidth={78}
            collapsed={
              collapsed
            }
            trigger={
              null
            }
            style={{
              position:
                "fixed",

              top: 0,
              bottom: 0,
              left: 0,

              height:
                "100vh",

              zIndex:
                100,

              overflow:
                "hidden",

              background:
                COLORS.sidebar,
            }}
          >
            {sidebar}
          </Sider>
        )}


        {/* =================================================
           MOBILE DRAWER
        ================================================= */}

        {isMobile && (
          <Drawer
            open={
              drawerOpen
            }
            placement="left"
            width={310}
            closable={false}
            onClose={() =>
              setDrawerOpen(
                false
              )
            }
            styles={{
              body: {
                padding:
                  0,

                background:
                  COLORS.sidebar,

                overflow:
                  "hidden",
              },
            }}
          >

            <div
              style={{
                position:
                  "absolute",

                top:
                  13,

                right:
                  13,

                zIndex:
                  20,
              }}
            >
              <Button
                type="text"
                icon={
                  <CloseOutlined />
                }
                onClick={() =>
                  setDrawerOpen(
                    false
                  )
                }
                style={{
                  width:
                    34,

                  height:
                    34,

                  color:
                    "#D9E7EC",

                  background:
                    "rgba(34,197,94,.10)",

                  border:
                    "1px solid rgba(74,222,128,.15)",

                  borderRadius:
                    8,

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",
                }}
              />
            </div>

            {sidebar}
          </Drawer>
        )}


        {/* =================================================
           MAIN LAYOUT
        ================================================= */}

        <Layout
          style={{
            marginLeft:
              isMobile
                ? 0
                : collapsed
                  ? 78
                  : 292,

            height:
              "100vh",

            minHeight:
              "100vh",

            background:
              theme.background,

            transition:
              "margin-left 180ms ease",
          }}
        >

          {/* =================================================
             HEADER
          ================================================= */}

          <Header
            style={{
              height:
                68,

              minHeight:
                68,

              flexShrink:
                0,

              padding:
                isMobile
                  ? "0 9px"
                  : "0 16px",

              display:
                "flex",

              alignItems:
                "center",

              gap:
                isMobile
                  ? 6
                  : 10,

              background:
                theme.header,

              borderBottom:
                `1px solid ${theme.border}`,

              position:
                "relative",

              zIndex:
                30,
            }}
          >

            {isNavigating && (
              <>
                <div className="college-navigation-progress" />

                <div className="college-loading-label">
                  Opening{" "}
                  {
                    menuLookup[
                      pendingPage
                    ]?.label ||
                    "module"
                  }
                </div>
              </>
            )}


            {/* Menu */}
            <Button
              type="text"
              className="college-menu-button"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (isMobile) {
                  setDrawerOpen(
                    true
                  );
                } else {
                  setCollapsed(
                    (value) =>
                      !value
                  );
                }
              }}
            />


            {/* User Search */}
            <div
              className="college-search-box"
              style={{
                flex:
                  1,

                minWidth:
                  0,

                maxWidth:
                  isMobile
                    ? 270
                    : 450,
              }}
            >
              <UserSearchBox
                theme={
                  theme
                }
                setSelectedUser={
                  setSelectedUser
                }
                setActiveComponent={
                  setActivePage
                }
              />
            </div>


            <div
              style={{
                flex:
                  1,

                minWidth:
                  0,
              }}
            />


            {/* =================================================
               HEADER ACTIONS
            ================================================= */}

            <div className="college-header-actions">

              {/* Password */}
              <Tooltip title="User Password Settings">
                <button
                  type="button"
                  className="college-header-action"
                  onClick={() =>
                    setPasswordSettingOpen(
                      true
                    )
                  }
                  aria-label="User Password Settings"
                >
                  <SafetyCertificateOutlined />
                </button>
              </Tooltip>


              {/* Academic Year */}
              <Tooltip title="Add Academic Year">
                <button
                  type="button"
                  className="college-academic-year"
                  onClick={() =>
                    setOpenAcademyYear(
                      true
                    )
                  }
                  disabled={
                    academyYearRefreshing
                  }
                  aria-label="Add Academic Year"
                >
                  <PlusOutlined />

                  <span className="college-academic-year-text">
                    Add Academic Year
                  </span>
                </button>
              </Tooltip>


              {/* Tasks */}
              <Tooltip title="Tasks">
                <button
                  type="button"
                  className="college-header-action"
                  onClick={() =>
                    setTodoOpen(
                      true
                    )
                  }
                  aria-label="Tasks"
                >
                  <CheckSquareOutlined />
                </button>
              </Tooltip>


              <PostTodoList
                open={
                  todoOpen
                }
                setOpen={
                  setTodoOpen
                }
              />


              {/* Dark Mode */}
              <Darkmode
                darkMode={
                  darkMode
                }
                setDarkMode={
                  setDarkMode
                }
              />


              {/* Notifications */}
              {!isMobile && (
                <>
                  <NotificationIcon
                    theme={
                      theme
                    }
                  />

                  <Notice />
                </>
              )}


              {/* Profile */}
              <Tooltip title="Profile">
                <div className="college-profile-container">
                  <div className="college-profile-avatar">
                    <ProfileImage
                      theme={
                        theme
                      }
                      header
                    />
                  </div>
                </div>
              </Tooltip>

            </div>
          </Header>


          {/* =================================================
             CONTENT
          ================================================= */}

          <Content
            style={{
              background:
                theme.background,

              padding:
                isMobile
                  ? "9px 8px 12px"
                  : "14px 16px 18px",

              minHeight:
                0,

              overflowY:
                "auto",

              overflowX:
                "hidden",
            }}
          >

            {/* Page heading */}
            <div className="college-page-heading">

              <div className="college-page-heading-left">

                <span className="college-page-heading-line" />

                <span className="college-page-heading-title">
                  {
                    activeTitle
                  }
                </span>

              </div>


              {!isMobile && (
                <span className="college-page-heading-meta">
                  IGNITE • ADMISSION OFFICE • STUDENT SERVICES
                </span>
              )}

            </div>


            {/* =================================================
               WORKSPACE
            ================================================= */}

            <motion.div
              key={
                selectedUser
                  ? "user-details"
                  : activePage
              }

              initial={{
                opacity:
                  0,

                y:
                  6,
              }}

              animate={{
                opacity:
                  1,

                y:
                  0,
              }}

              transition={{
                duration:
                  0.2,

                ease:
                  "easeOut",
              }}

              className={
                darkMode
                  ? "college-workspace college-workspace-dark"
                  : "college-workspace"
              }

              style={{
                padding:
                  isMobile
                    ? 9
                    : 15,
              }}
            >
              {renderPage()}
            </motion.div>

          </Content>

        </Layout>
      </Layout>


      {/* =====================================================
         PASSWORD SETTINGS
      ===================================================== */}

      <Modal
        open={
          passwordSettingOpen
        }
        onCancel={() =>
          setPasswordSettingOpen(
            false
          )
        }
        footer={null}
        centered
        width={500}
        destroyOnClose
        className="college-password-modal"
        title={null}
        styles={{
          content: {
            padding:
              0,

            borderRadius:
              12,

            overflow:
              "hidden",
          },

          body: {
            padding:
              0,
          },
        }}
      >
        <ChangeUserPassword
          onClose={() =>
            setPasswordSettingOpen(
              false
            )
          }
        />
      </Modal>


      {/* =====================================================
         ADD ACADEMIC YEAR
      ===================================================== */}

      <AddAcademyYear
        open={
          openAcademyYear
        }
        handleClose={() =>
          setOpenAcademyYear(
            false
          )
        }
        refreshYears={
          fetchAcademicYears
        }
      />

    </div>
  );
};

export default AdmissionOfficialDashboard;
